import { ThreadAnalysisResultItem } from "@coeff/api";
import { Empty, Tag, Typography } from "antd";
import React from "react";
import styled from "styled-components";

const { Text } = Typography;

// result is free-form JSON — its shape is owned by the analysis prompt.
type AnalysisResult = NonNullable<ThreadAnalysisResultItem["result"]>;

const Card = styled.div<{ $clickable?: boolean }>`
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 8px 10px;
  background: #fff;
  cursor: ${p => (p.$clickable ? "pointer" : "default")};

  &:hover {
    border-color: ${p => (p.$clickable ? "#1890ff" : "#e8e8e8")};
  }

  & + & {
    margin-top: 6px;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #444;
  margin: 14px 0 6px;

  &:first-child {
    margin-top: 0;
  }
`;

const Why = styled.div`
  color: #666;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
`;

const STATE_COLOR: Record<string, string> = {
  yes: "green",
  partially: "orange",
  no: "red",
  abandoned: "red",
};

const FRUSTRATION_COLOR: Record<string, string> = { high: "red", mild: "orange" };

function asRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? (value.filter(v => v && typeof v === "object" && !Array.isArray(v)) as Record<
        string,
        unknown
      >[])
    : [];
}

function str(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

/** The prompt's anchor field: which [U<n>] turn the user first raised this on. */
function firstUserTurn(item: Record<string, unknown>): number | undefined {
  const raw = item.first_user_turn;
  return typeof raw === "number" && Number.isFinite(raw) ? raw : undefined;
}

/** True when this result carries per-finding anchors, i.e. clicking can jump to a message. */
export function hasTurnAnchors(result?: AnalysisResult): boolean {
  if (!result) {
    return false;
  }
  return asRecords(result.struggles).some(
    s => firstUserTurn(s) !== undefined || !!str(s.user_quote)
  );
}

interface FindingsListProps {
  result?: AnalysisResult;
  onJump: (anchor: { turn?: number; quote?: string }) => void;
}

/**
 * Struggles and one-turn issues as compact cards. A struggle that carries a first_user_turn is
 * clickable and scrolls the transcript to where the user raised it.
 *
 * first_user_turn is the anchor; user_quote is a fallback, not a veto. In the first production
 * run one thread came back with first_user_turn 0 on every finding while its quotes pointed at
 * the right messages, so both are passed through and resolveAnchor prefers the turn.
 */
export const FindingsList: React.FC<FindingsListProps> = ({ result, onJump }) => {
  const struggles = asRecords(result?.struggles);
  const oneTurnIssues = asRecords(result?.one_turn_issues);

  if (!struggles.length && !oneTurnIssues.length) {
    return <Empty description="No findings" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div>
      {struggles.length > 0 && <SectionTitle>Struggles ({struggles.length})</SectionTitle>}
      {struggles.map((s, i) => {
        const turn = firstUserTurn(s);
        const quote = str(s.user_quote);
        const resolved = str(s.resolved);
        const frustration = str(s.user_frustration);
        const anchored = turn !== undefined || !!quote;
        return (
          <Card
            key={`struggle-${i}`}
            $clickable={anchored}
            onClick={anchored ? () => onJump({ turn, quote }) : undefined}
          >
            <div>
              {turn !== undefined && turn > 0 && <Tag color="blue">U{turn}</Tag>}
              {s.turns_spent !== undefined && <Tag>{str(s.turns_spent)} turns</Tag>}
              {resolved && <Tag color={STATE_COLOR[resolved]}>{resolved}</Tag>}
              {frustration && frustration !== "none" && (
                <Tag color={FRUSTRATION_COLOR[frustration]}>{frustration}</Tag>
              )}
              {s.theme !== undefined && <Tag>{str(s.theme)}</Tag>}
            </div>
            <Text style={{ fontSize: 13 }}>{str(s.task)}</Text>
            {s.why_it_dragged !== undefined && <Why>{str(s.why_it_dragged)}</Why>}
            {quote && (
              <Why>
                <Text type="secondary" italic style={{ fontSize: 12 }}>
                  “{quote}”
                </Text>
              </Why>
            )}
          </Card>
        );
      })}

      {oneTurnIssues.length > 0 && (
        <SectionTitle>One-turn issues ({oneTurnIssues.length})</SectionTitle>
      )}
      {oneTurnIssues.map((issue, i) => {
        const fixed = str(issue.fixed);
        return (
          <Card key={`issue-${i}`}>
            <div>
              {fixed && <Tag color={STATE_COLOR[fixed]}>fixed: {fixed}</Tag>}
              {issue.theme !== undefined && <Tag>{str(issue.theme)}</Tag>}
            </div>
            <Text style={{ fontSize: 13 }}>{str(issue.task)}</Text>
            {issue.evidence !== undefined && <Why>{str(issue.evidence)}</Why>}
          </Card>
        );
      })}
    </div>
  );
};
